import { HttpClient } from '@adapters/HttpClient';
import {
    TopupResponse,
    TopupsResponse,
    CreateTopupPayload,
    TopupsFilters,
    ApproveTopupPayload,
    RejectTopupPayload,
} from '../models';

const buildAdvancedFiltersParams = (advancedFilters: TopupsFilters['advanced_filters']) => {
    if (!advancedFilters) return {};
    const params: Record<string, string | number> = {};

    advancedFilters.filters.forEach((filter, index) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;

        if (
            filter.value === undefined ||
            filter.value === null ||
            (Array.isArray(filter.value) && filter.value.length === 0)
        ) {
            return;
        }

        if (Array.isArray(filter.value)) {
            filter.value.forEach((value, valueIndex) => {
                params[`advanced_filters[filters][${index}][value][${valueIndex}]`] = value;
            });
            return;
        }

        params[`advanced_filters[filters][${index}][value]`] = filter.value;
    });

    if (advancedFilters.logic) {
        params['advanced_filters[logic]'] = advancedFilters.logic;
    }

    return params;
};

export class TopupsApiAdapter {
    constructor(private httpClient: HttpClient) { }

    async getTopups(walletId: number | null, filters: TopupsFilters = {}): Promise<TopupsResponse> {
        const { sorting, advanced_filters } = filters;
        const sortColumn = sorting?.column ?? 'id';
        const sortDirection = sorting?.direction ?? 'desc';

        const params: Record<string, string | number> = {
            'sorting[column]': sortColumn,
            'sorting[direction]': sortDirection,
        };

        if (walletId) {
            params['wallet_id'] = walletId;
        }

        if (advanced_filters && advanced_filters.filters.length > 0) {
            Object.assign(
                params,
                buildAdvancedFiltersParams({
                    logic: advanced_filters.logic ?? 'AND',
                    filters: advanced_filters.filters,
                })
            );
        }

        return await this.httpClient.get<TopupsResponse>(
            `/topups`,
            params,
            {},
            true
        );
    }

    async getTopup(id: number): Promise<TopupResponse> {
        return await this.httpClient.get<TopupResponse>(
            `/topups/${id}`,
            {},
            {},
            true
        );
    }

    async createTopup(walletId: number, payload: CreateTopupPayload): Promise<TopupResponse> {
        const formData = new FormData();

        formData.append('declared_amount', payload.declared_amount.toString());
        formData.append('payment_method', payload.payment_method);
        formData.append('payment_reference', payload.payment_reference);
        formData.append('transfer_date', payload.transfer_date);
        formData.append('payer_name', payload.payer_name);
        formData.append('payer_iban', payload.payer_iban);
        formData.append('internal_note', payload.internal_note);

        if (payload.proof_document_file) {
            formData.append('proof_document_file', payload.proof_document_file);
        }

        return await this.httpClient.post<TopupResponse>(
            `/wallets/${walletId}/topups`,
            formData,
            true,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
            }
        );
    }

    async approveTopup(id: number, payload?: ApproveTopupPayload): Promise<TopupResponse> {
        return await this.httpClient.put<TopupResponse>(
            `/topups/${id}/approve`,
            payload ?? {}
        );
    }

    async rejectTopup(id: number, payload: RejectTopupPayload): Promise<TopupResponse> {
        return await this.httpClient.put<TopupResponse>(
            `/topups/${id}/reject`,
            payload
        );
    }
}

